<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  templateId: {
    type: Number,
    required: true,
  },
  availableVariables: {
    type: Array,
    default: () => [],
  },
  delayOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['deleted', 'updated']);

const store = useStore();
const { t } = useI18n();

const isExpanded = ref(false);
const isSubmitting = ref(false);
const isDeleting = ref(false);

// Inline edit form
const editContent = ref('');
const editDelay = ref(0);
const pendingAttachment = ref(null);
const fileInputRef = ref(null);

const toggleExpand = () => {
  if (!isExpanded.value) {
    editContent.value = props.item.content || '';
    editDelay.value = props.item.delay_seconds || 0;
    pendingAttachment.value = null;
  }
  isExpanded.value = !isExpanded.value;
};

const getItemIcon = itemType => {
  const icons = {
    text: 'i-lucide-type',
    image: 'i-lucide-image',
    audio: 'i-lucide-music',
    video: 'i-lucide-video',
    document: 'i-lucide-file-text',
    whatsapp_template: 'i-lucide-message-square-text',
  };
  return icons[itemType] || 'i-lucide-file';
};

const getDelayLabel = seconds => {
  if (!seconds || seconds === 0) return t('FOLLOW_UP_ITEMS.DELAY.NO_DELAY');
  if (seconds < 60)
    return t('FOLLOW_UP_ITEMS.DELAY.SECONDS', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  return t('FOLLOW_UP_ITEMS.DELAY.MINUTES', { count: minutes });
};

const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// File sent in the template's media header, when the item has one.
const templateHeaderMedia = computed(() => {
  const header = props.item.whatsapp_template_mapping?.header;
  return header?.media_url ? header : null;
});

const contentPreview = computed(() => {
  if (props.item.item_type === 'text') {
    return props.item.content || t('FOLLOW_UP_BUILDER.ITEMS.EMPTY_TEXT');
  }
  if (props.item.item_type === 'whatsapp_template') {
    return props.item.whatsapp_template_name || props.item.content || '';
  }
  if (props.item.attachment) {
    return props.item.attachment.filename;
  }
  return t('FOLLOW_UP_ITEMS.NO_ATTACHMENT');
});

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const handleFileSelect = event => {
  const file = event.target.files[0];
  if (!file) return;

  const maxSize = 20 * 1024 * 1024;
  if (file.size > maxSize) {
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.FILE_TOO_LARGE'));
    return;
  }

  pendingAttachment.value = file;
  event.target.value = '';
};

const removePendingAttachment = () => {
  pendingAttachment.value = null;
};

const insertVariable = variable => {
  editContent.value += variable;
};

const handleSave = async () => {
  // Validate content for text items
  if (props.item.item_type === 'text' && !editContent.value.trim().length) {
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.CONTENT_REQUIRED'));
    return;
  }

  isSubmitting.value = true;
  try {
    await store.dispatch('followUpTemplates/updateItem', {
      templateId: props.templateId,
      itemId: props.item.id,
      data: {
        content: editContent.value,
        delay_seconds: editDelay.value,
        item_type: props.item.item_type,
      },
      attachment: pendingAttachment.value,
    });
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.UPDATE_SUCCESS'));
    isExpanded.value = false;
    emit('updated');
  } catch (error) {
    const errorMessage =
      error.response?.data?.errors?.join(', ') ||
      t('FOLLOW_UP_ITEMS.MESSAGES.ERROR');
    useAlert(errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

const handleDelete = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('followUpTemplates/deleteItem', {
      templateId: props.templateId,
      itemId: props.item.id,
    });
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.DELETE_SUCCESS'));
    emit('deleted');
  } catch {
    useAlert(t('FOLLOW_UP_ITEMS.MESSAGES.ERROR'));
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg transition-all duration-200"
    :class="{ 'ring-2 ring-woot-500/30': isExpanded }"
  >
    <!-- Collapsed Row -->
    <div
      class="flex items-center gap-3 p-3 cursor-pointer"
      @click="toggleExpand"
    >
      <!-- Drag Handle -->
      <button
        type="button"
        class="drag-handle p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-grab active:cursor-grabbing"
        @click.stop
      >
        <span class="i-lucide-grip-vertical size-5" />
      </button>

      <!-- Position Badge -->
      <div
        class="flex-shrink-0 w-6 h-6 rounded-full bg-woot-100 dark:bg-woot-900/30 flex items-center justify-center"
      >
        <span class="text-xs font-semibold text-woot-600 dark:text-woot-400">
          {{ index + 1 }}
        </span>
      </div>

      <!-- Type Icon -->
      <div
        class="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center"
      >
        <span
          class="size-4 text-slate-500"
          :class="getItemIcon(item.item_type)"
        />
      </div>

      <!-- Content Preview -->
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span
            class="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase"
          >
            {{ t(`FOLLOW_UP_ITEMS.TYPES.${item.item_type.toUpperCase()}`) }}
          </span>
          <span
            v-if="item.delay_seconds > 0"
            class="px-1.5 py-0.5 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded"
          >
            {{ getDelayLabel(item.delay_seconds) }}
          </span>
        </div>
        <p class="text-sm text-slate-700 dark:text-slate-300 truncate mt-0.5">
          {{ contentPreview }}
        </p>
      </div>

      <!-- Quick Actions -->
      <div class="flex items-center gap-1 flex-shrink-0" @click.stop>
        <button
          type="button"
          class="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded transition-colors"
          @click="toggleExpand"
        >
          <span
            class="size-4"
            :class="
              isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>
        <button
          type="button"
          class="p-1.5 text-slate-400 hover:text-red-500 rounded transition-colors"
          :disabled="isDeleting"
          @click="handleDelete"
        >
          <span
            v-if="isDeleting"
            class="i-svg-spinners-90-ring-with-bg size-4"
          />
          <span v-else class="i-lucide-trash-2 size-4" />
        </button>
      </div>
    </div>

    <!-- Expanded Edit Form -->
    <div
      v-if="isExpanded"
      class="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-700 space-y-3"
      @click.stop
    >
      <!-- Text Content -->
      <div v-if="item.item_type === 'text'">
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.CONTENT_LABEL') }}
        </label>
        <textarea
          v-model="editContent"
          rows="3"
          :placeholder="t('FOLLOW_UP_ITEMS.MODAL.CONTENT_PLACEHOLDER')"
          class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 focus:border-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 resize-none text-sm"
        />
        <!-- Variables -->
        <div
          v-if="availableVariables.length > 0"
          class="mt-2 flex flex-wrap gap-1"
        >
          <button
            v-for="variable in availableVariables"
            :key="variable"
            type="button"
            class="px-2 py-0.5 text-xs bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-500 transition-colors"
            @click="insertVariable(variable)"
          >
            {{ variable }}
          </button>
        </div>
      </div>

      <!-- WhatsApp template (read-only summary; edit via delete + re-add) -->
      <div v-else-if="item.item_type === 'whatsapp_template'" class="space-y-2">
        <div class="flex items-center gap-2">
          <span
            class="px-2 py-0.5 text-xs font-medium bg-n-teal-3 text-n-teal-11 rounded"
          >
            {{ item.whatsapp_template_name }}
            <template v-if="item.whatsapp_template_language">
              ({{ item.whatsapp_template_language }})
            </template>
          </span>
        </div>
        <!-- Media header file: read-only, so the attachment is verifiable
             without deleting and re-adding the item. -->
        <div
          v-if="templateHeaderMedia"
          class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
          :title="templateHeaderMedia.media_url"
        >
          <span class="i-lucide-paperclip size-3.5 flex-shrink-0" />
          <span class="truncate">
            {{
              templateHeaderMedia.media_name || templateHeaderMedia.media_url
            }}
          </span>
        </div>
        <div>
          <label
            class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
          >
            {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.FALLBACK_LABEL') }}
          </label>
          <p
            class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2"
          >
            {{ item.content }}
          </p>
        </div>
      </div>

      <!-- Media Attachment -->
      <div v-else>
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.ATTACHMENT_LABEL') }}
        </label>

        <!-- Hidden file input -->
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*,audio/*,video/mp4,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          class="hidden"
          @change="handleFileSelect"
        />

        <!-- Pending new attachment -->
        <div
          v-if="pendingAttachment"
          class="flex items-center gap-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
        >
          <span
            class="size-5 text-blue-500"
            :class="getItemIcon(item.item_type)"
          />
          <div class="flex-1 min-w-0">
            <p
              class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate"
            >
              {{ pendingAttachment.name }}
            </p>
            <p class="text-xs text-slate-500">
              {{ formatFileSize(pendingAttachment.size) }}
            </p>
          </div>
          <button
            type="button"
            class="p-1 text-slate-400 hover:text-red-500"
            @click="removePendingAttachment"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <!-- Current attachment -->
        <div
          v-else-if="item.attachment"
          class="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-700 rounded-lg"
        >
          <span
            class="size-5 text-slate-500"
            :class="getItemIcon(item.item_type)"
          />
          <div class="flex-1 min-w-0">
            <p
              class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate"
            >
              {{ item.attachment.filename }}
            </p>
            <p class="text-xs text-slate-500">
              {{ formatFileSize(item.attachment.byte_size) }}
            </p>
          </div>
          <button
            type="button"
            class="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
            @click="triggerFileInput"
          >
            {{ t('FOLLOW_UP_ITEMS.MODAL.REPLACE') }}
          </button>
        </div>

        <!-- Upload prompt -->
        <button
          v-else
          type="button"
          class="w-full px-4 py-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-slate-400 transition-colors flex items-center justify-center gap-2 text-slate-500"
          @click="triggerFileInput"
        >
          <span class="i-lucide-upload size-5" />
          <span class="text-sm font-medium">
            {{ t('FOLLOW_UP_ITEMS.MODAL.UPLOAD_FILE') }}
          </span>
        </button>

        <!-- Optional caption for media -->
        <div
          v-if="item.item_type === 'image' || item.item_type === 'video'"
          class="mt-2"
        >
          <input
            v-model="editContent"
            type="text"
            :placeholder="t('FOLLOW_UP_BUILDER.ITEMS.CAPTION_PLACEHOLDER')"
            class="w-full h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 text-sm"
          />
        </div>
      </div>

      <!-- Delay -->
      <div>
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.DELAY_LABEL') }}
        </label>
        <select
          v-model.number="editDelay"
          class="w-full h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-woot-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm"
        >
          <option
            v-for="option in delayOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ t(option.label) }}
          </option>
        </select>
      </div>

      <!-- Save / Cancel buttons -->
      <div class="flex gap-2 pt-1">
        <NextButton
          size="xs"
          variant="faded"
          color="slate"
          class="flex-1"
          @click="isExpanded = false"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.CANCEL') }}
        </NextButton>
        <NextButton
          size="xs"
          variant="solid"
          color="blue"
          class="flex-1"
          :is-loading="isSubmitting"
          @click="handleSave"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.UPDATE') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
